//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { RefObject, useEffect, useState } from 'react';

interface Props {
  coverImageUrl?: string;
  isMediaHovered: boolean;
  ref: RefObject<HTMLVideoElement>;
  shouldIgnoreOffsetOnPlay?: boolean;
  shouldPlayOnHover: boolean;
  shouldStartMuted: boolean;
  thumbnailOffsetSeconds: number;
  videoUrl: string;
}

export const useVideo = (props: Props) => {
  const {
    coverImageUrl,
    isMediaHovered,
    ref,
    shouldIgnoreOffsetOnPlay,
    shouldPlayOnHover,
    shouldStartMuted,
    thumbnailOffsetSeconds,
    videoUrl,
  } = props;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(shouldStartMuted);

  useEffect(() => {
    if (ref.current) {
      ref.current.load();
      if (thumbnailOffsetSeconds > 0) {
        ref.current.currentTime = thumbnailOffsetSeconds;
      }
      setIsPlaying(false);
    }
  }, [ref, thumbnailOffsetSeconds, videoUrl, coverImageUrl]); // when videoUrl or coverImageUrl change we want to reload the video to reset to the thumbnail

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    void (async () => {
      if (ref.current && shouldPlayOnHover) {
        if (isMediaHovered) {
          setIsPlaying(true);
          await ref.current.play();
        } else {
          ref.current.pause();
          timeoutId = setTimeout(() => {
            setIsPlaying(false);
          }, 150);
        }
      }
    })();
    return () => {
      clearTimeout(timeoutId);
    };
  }, [ref, isMediaHovered, shouldPlayOnHover]);

  // The video `pause` method does not return a promise, but is async
  // If you try to play while pausing is in progress, Chrome throws an error
  // https://stackoverflow.com/a/36898221
  const togglePlayVideo = async () => {
    const currentVideoRef = ref.current;

    if (currentVideoRef) {
      currentVideoRef.muted = shouldStartMuted;
      if (currentVideoRef.paused || currentVideoRef.ended || !isPlaying) {
        try {
          // The `played` value is a TimeRanges object representing all of the sections of the
          // video that have been seen already. If the length is 0, it's never been played.
          // https://www.w3schools.com/jsref/prop_video_played.asp
          const hasPlayedVideo = currentVideoRef.played.length > 0;
          if (!hasPlayedVideo) {
            // We use the current time to mimick a poster image at the selected offset,
            // so reset to the thumbnail offset (or beginning if shouldIgnoreOffsetOnPlay)
            // if this is our first time playing the video
            currentVideoRef.currentTime = shouldIgnoreOffsetOnPlay
              ? 0
              : thumbnailOffsetSeconds;
          } else if (
            shouldIgnoreOffsetOnPlay &&
            currentVideoRef.currentTime === thumbnailOffsetSeconds
          ) {
            currentVideoRef.currentTime = 0;
          }

          setIsPlaying(true);
          await currentVideoRef.play();
        } catch (e) {
          // swallow the error here
        }
        setIsPlaying(true);
      } else {
        currentVideoRef.pause();
        setTimeout(() => {
          setIsPlaying(false);
        }, 150);
      }
    }
  };

  const toggleVolume = () => {
    if (ref.current) {
      if (ref.current.muted) {
        ref.current.muted = false;
        setIsMuted(false);
      } else {
        ref.current.muted = true;
        setIsMuted(true);
      }
    }
  };

  return {
    isPlaying,
    setIsPlaying,
    isMuted,
    setIsMuted,
    toggleVolume,
    togglePlayVideo,
  };
};
